import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Search, Loader2, Package, FileText, Truck, Receipt,
  ChevronRight, ArrowLeft, ClipboardList, DollarSign,
  CheckCircle, AlertTriangle, Database, Globe, ArrowUpDown,
  ListFilter, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { OCNumber } from '../../lib/ocFormat';

const fmt = (v) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const fmtDate = (raw) => {
  if (!raw) return '-';
  const s = String(raw).trim().replace(/\s+0$/, '');
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    let y = parseInt(m[3], 10);
    if (y < 100) y += y > 50 ? 1900 : 2000;
    return new Date(y, parseInt(m[1], 10) - 1, parseInt(m[2], 10)).toLocaleDateString('es-CL');
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('es-CL');
};

function KpiCard({ title, value, sub, icon: Icon, color, testId }) {
  return (
    <Card className="shadow-sm" data-testid={testId}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-[11px] text-muted-foreground leading-tight">{title}</p>
          <p className="text-lg font-bold">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function FlowBadge({ estado }) {
  const map = {
    emitida: { bg: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Emitida' },
    recibida: { bg: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Recibida' },
    contabilizada: { bg: 'bg-green-100 text-green-800 border-green-200', label: 'Contabilizada' },
  };
  const cfg = map[estado] || map.emitida;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg}`}
      data-testid={`flow-badge-${estado}`}>
      {cfg.label}
    </span>
  );
}

function OrigenBadge({ source }) {
  if (source === 'web') return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
      <Globe className="w-2.5 h-2.5" /> Web
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-100 text-sky-700 border border-sky-200">
      <Database className="w-2.5 h-2.5" /> Access
    </span>
  );
}

function StepIndicator({ steps }) {
  return (
    <div className="flex items-center gap-0.5">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center gap-0.5">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold
            ${s.done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`} title={s.label}>
            {s.done ? <CheckCircle className="w-3 h-3" /> : i + 1}
          </div>
          {i < steps.length - 1 && <div className={`w-4 h-0.5 ${s.done ? 'bg-green-400' : 'bg-gray-200'}`} />}
        </div>
      ))}
    </div>
  );
}

/* ─── Sortable column header ─── */
function SortHeader({ label, field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <button onClick={() => onSort(field)} className="flex items-center gap-0.5 hover:text-white/90 group">
      {label}
      {active ? (
        sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40 group-hover:opacity-80" />
      )}
    </button>
  );
}

/* ─── Reusable items table ─── */
function ItemsTable({ items, sortBy, sortDir, onSort, showTipo = false }) {
  const tipoBadge = { material: 'bg-indigo-100 text-indigo-700', servicio: 'bg-purple-100 text-purple-700', gasto: 'bg-amber-100 text-amber-700', sin_clasificar: 'bg-gray-100 text-gray-600' };
  const tipoLabel = { material: 'MAT', servicio: 'SRV', gasto: 'GTO', sin_clasificar: '?' };
  return (
    <table className="w-full text-xs">
      <thead className="sticky top-14 lg:top-0 z-20">
        <tr className="bg-gray-800 text-white text-[11px]">
          <th className="px-2 py-2.5 text-left w-8">#</th>
          {showTipo && <th className="px-2 py-2.5 text-center w-12">Tipo</th>}
          <th className="px-2 py-2.5 text-left min-w-[200px]">
            <SortHeader label="Descripcion" field="descripcion" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
          <th className="px-2 py-2.5 text-center w-12">Und</th>
          <th className="px-2 py-2.5 text-right w-16">
            <SortHeader label="Cant." field="cantidad" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
          <th className="px-2 py-2.5 text-right w-20">V.Unit.</th>
          <th className="px-2 py-2.5 text-right w-24">
            <SortHeader label="Total" field="total_linea" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
          <th className="px-2 py-2.5 text-center w-16">
            <SortHeader label="OC" field="numero_oc" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
          <th className="px-2 py-2.5 text-left min-w-[120px]">
            <SortHeader label="Proveedor" field="proveedor" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
          <th className="px-2 py-2.5 text-center w-20">Fecha OC</th>
          <th className="px-2 py-2.5 text-center w-24">
            <SortHeader label="Estado" field="estado" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
          <th className="px-2 py-2.5 text-center w-14">
            <SortHeader label="Dias" field="dias_pendiente" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={it.codigo || `item-${i}`} className={`border-t hover:bg-gray-50 ${(it.dias_pendiente || 0) > 30 ? 'bg-red-50/40' : ''}`} data-testid={`item-row-${i}`}>
            <td className="px-2 py-1.5 text-gray-400 text-center">{i + 1}</td>
            {showTipo && (
              <td className="px-2 py-1.5 text-center">
                <span className={`px-1 py-0.5 rounded text-[9px] font-semibold ${tipoBadge[it.tipo] || tipoBadge.sin_clasificar}`}>
                  {tipoLabel[it.tipo] || '?'}
                </span>
              </td>
            )}
            <td className="px-2 py-1.5">
              <div className="font-medium">{it.descripcion}</div>
              {it.observaciones && <div className="text-[10px] text-gray-400 italic truncate max-w-[250px]">{it.observaciones}</div>}
            </td>
            <td className="px-2 py-1.5 text-center">{it.unidad}</td>
            <td className="px-2 py-1.5 text-right font-mono">{it.cantidad}</td>
            <td className="px-2 py-1.5 text-right font-mono text-gray-600">{fmt(it.valor_unitario)}</td>
            <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmt(it.total_linea)}</td>
            <td className="px-2 py-1.5 text-center font-mono font-bold text-indigo-700"><OCNumber oc={it} size="sm" /></td>
            <td className="px-2 py-1.5 text-gray-600 truncate max-w-[160px]">{it.proveedor || '-'}</td>
            <td className="px-2 py-1.5 text-center text-gray-500">{fmtDate(it.fecha_oc)}</td>
            <td className="px-2 py-1.5 text-center"><FlowBadge estado={it.estado} /></td>
            <td className="px-2 py-1.5 text-center">
              {it.dias_pendiente != null ? (
                <span className={`font-mono text-[11px] font-semibold ${it.dias_pendiente > 30 ? 'text-red-600' : it.dias_pendiente > 15 ? 'text-orange-600' : 'text-gray-500'}`}>
                  {it.dias_pendiente}d
                </span>
              ) : <span className="text-gray-300">-</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ═══════════════════════════════════════════════ */
export default function TrazabilidadOT() {
  const [otSearch, setOtSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [otList, setOtList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'detail'

  // Items tab state
  const [detailTab, setDetailTab] = useState('flujo'); // 'flujo' | 'items'
  const [itemsData, setItemsData] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsSortBy, setItemsSortBy] = useState('descripcion');
  const [itemsSortDir, setItemsSortDir] = useState('asc');
  const [itemsEstadoFilter, setItemsEstadoFilter] = useState('');
  const [itemsTipoFilter, setItemsTipoFilter] = useState('');
  const [itemsSearch, setItemsSearch] = useState('');
  const [itemsViewMode, setItemsViewMode] = useState('tipo'); // 'tipo' | 'familia' | 'tabla'

  const loadOtList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/compras/trazabilidad');
      setOtList(res.data.ots || []);
    } catch {
      toast.error('Error cargando OTs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOtList(); }, [loadOtList]);

  const loadDetail = async (otNum) => {
    setLoading(true);
    setDetailTab('flujo');
    setItemsData(null);
    try {
      const res = await api.get(`/compras/trazabilidad?ot=${otNum}`);
      setDetail(res.data);
      setView('detail');
    } catch {
      toast.error('Error cargando trazabilidad');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (otNum) => {
    setItemsLoading(true);
    try {
      const res = await api.get(`/compras/trazabilidad/items?ot=${otNum}`);
      setItemsData(res.data);
    } catch {
      toast.error('Error cargando items');
    } finally {
      setItemsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setDetailTab(tab);
    if (tab === 'items' && !itemsData && detail?.ot) {
      loadItems(detail.ot);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (otSearch.trim()) loadDetail(otSearch.trim());
  };

  const handleItemsSort = (field) => {
    if (itemsSortBy === field) {
      setItemsSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setItemsSortBy(field);
      setItemsSortDir('asc');
    }
  };

  const sortedItems = useMemo(() => {
    if (!itemsData?.items) return [];
    let list = [...itemsData.items];

    // Filter by tipo
    if (itemsTipoFilter) list = list.filter(i => i.tipo === itemsTipoFilter);

    // Filter by estado
    if (itemsEstadoFilter) list = list.filter(i => i.estado === itemsEstadoFilter);

    // Filter by search
    if (itemsSearch.trim()) {
      const q = itemsSearch.toLowerCase();
      list = list.filter(i =>
        (i.descripcion || '').toLowerCase().includes(q) ||
        (i.codigo || '').toLowerCase().includes(q) ||
        (i.proveedor || '').toLowerCase().includes(q) ||
        (i.concepto || '').toLowerCase().includes(q) ||
        String(i.numero_oc).includes(q)
      );
    }

    // Sort
    list.sort((a, b) => {
      let va, vb;
      switch (itemsSortBy) {
        case 'descripcion': va = (a.descripcion || '').toLowerCase(); vb = (b.descripcion || '').toLowerCase(); break;
        case 'numero_oc': va = a.numero_oc; vb = b.numero_oc; break;
        case 'estado': va = a.estado; vb = b.estado; break;
        case 'total_linea': va = a.total_linea || 0; vb = b.total_linea || 0; break;
        case 'cantidad': va = a.cantidad || 0; vb = b.cantidad || 0; break;
        case 'proveedor': va = (a.proveedor || '').toLowerCase(); vb = (b.proveedor || '').toLowerCase(); break;
        case 'dias_pendiente': va = a.dias_pendiente ?? -1; vb = b.dias_pendiente ?? -1; break;
        default: va = (a.descripcion || '').toLowerCase(); vb = (b.descripcion || '').toLowerCase();
      }
      if (va < vb) return itemsSortDir === 'asc' ? -1 : 1;
      if (va > vb) return itemsSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [itemsData, itemsSortBy, itemsSortDir, itemsEstadoFilter, itemsTipoFilter, itemsSearch]);

  const filteredOts = otSearch.trim()
    ? otList.filter(o => String(o.ot_numero).includes(otSearch.trim()) || (o.descripcion || '').toLowerCase().includes(otSearch.toLowerCase()))
    : otList;

  return (
    <div className="space-y-4" data-testid="trazabilidad-page">
      <div className="flex items-center gap-3">
        {view === 'detail' && (
          <button onClick={() => setView('list')} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="btn-back">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-indigo-600" />
            Trazabilidad por OT
          </h1>
          <p className="text-sm text-muted-foreground">
            {view === 'detail' && detail ? `OT ${detail.ot}` : 'Flujo Pedido → OC → Recepción → Factura'}
          </p>
        </div>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-md" data-testid="search-form">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar OT (numero o descripcion)..." value={otSearch}
            onChange={(e) => setOtSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="search-ot-input" />
        </div>
        <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="btn-search">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </form>

      {loading && !detail && !otList.length ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : view === 'list' ? (
        /* ───── OT List ───── */
        filteredOts.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No se encontraron OTs</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto" data-testid="ot-list-table">
            <table className="w-full text-xs">
              <thead className="sticky top-14 lg:top-0 z-20">
                <tr className="bg-gray-800 text-white">
                  <th className="px-3 py-2.5 text-left">OT</th>
                  <th className="px-3 py-2.5 text-left">Descripcion</th>
                  <th className="px-3 py-2.5 text-left">Cliente</th>
                  <th className="px-3 py-2.5 text-center">OCs</th>
                  <th className="px-3 py-2.5 text-right">Monto Total</th>
                  <th className="px-3 py-2.5 text-center">Accion</th>
                </tr>
              </thead>
              <tbody>
                {filteredOts.map((o, i) => (
                  <tr key={o.ot_numero} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => loadDetail(o.ot_numero)}
                    data-testid={`ot-row-${o.ot_numero}`}>
                    <td className="px-3 py-2 font-mono font-bold text-indigo-700">{o.ot_numero}</td>
                    <td className="px-3 py-2 max-w-xs truncate">{o.descripcion || '-'}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">{o.cliente || '-'}</td>
                    <td className="px-3 py-2 text-center font-semibold">{o.total_ocs}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(o.monto_total)}</td>
                    <td className="px-3 py-2 text-center">
                      <Button size="sm" variant="ghost" className="h-7 text-indigo-600 hover:text-indigo-800">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : detail ? (
        /* ───── OT Detail ───── */
        <div className="space-y-4" data-testid="trazabilidad-detail">
          {/* OT Info */}
          {detail.ot_info && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4" data-testid="ot-info-card">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div><span className="text-gray-500">OT</span><br /><strong className="text-lg text-indigo-700">{detail.ot_info.numero_ot}</strong></div>
                <div className="col-span-2"><span className="text-gray-500">Descripcion</span><br /><strong>{detail.ot_info.descripcion}</strong></div>
                <div><span className="text-gray-500">Cliente</span><br /><strong>{detail.ot_info.cliente || '-'}</strong></div>
                <div><span className="text-gray-500">Estado</span><br /><strong className="capitalize">{detail.ot_info.estado || '-'}</strong></div>
              </div>
            </div>
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
            <KpiCard title="Pedidos" value={detail.kpis.total_pedidos} icon={ClipboardList} color="bg-purple-50 text-purple-600" testId="kpi-pedidos" />
            <KpiCard title="OCs" value={detail.kpis.total_ocs} icon={FileText} color="bg-indigo-50 text-indigo-600" testId="kpi-ocs" />
            <KpiCard title="Monto OCs" value={fmt(detail.kpis.monto_total_ocs)} icon={DollarSign} color="bg-green-50 text-green-600" testId="kpi-monto" />
            <KpiCard title="Recepciones" value={detail.kpis.total_recepciones} icon={Truck} color="bg-blue-50 text-blue-600" testId="kpi-recepciones" />
            <KpiCard title="OCs Recibidas" value={detail.kpis.ocs_con_recepcion} icon={Package} color="bg-teal-50 text-teal-600" testId="kpi-recibidas" />
            <KpiCard title="Contabilizadas" value={detail.kpis.ocs_contabilizadas} icon={Receipt} color="bg-emerald-50 text-emerald-600" testId="kpi-contabilizadas" />
          </div>

          {/* Tabs: Flujo vs Items */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="detail-tabs">
            <button onClick={() => handleTabChange('flujo')} data-testid="tab-flujo"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${detailTab === 'flujo' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-600 hover:text-gray-800'}`}>
              <Package className="w-4 h-4" /> Flujo OC
            </button>
            <button onClick={() => handleTabChange('items')} data-testid="tab-items"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${detailTab === 'items' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-600 hover:text-gray-800'}`}>
              <ListFilter className="w-4 h-4" /> Items Detalle
            </button>
          </div>

          {/* ═══ FLUJO TAB ═══ */}
          {detailTab === 'flujo' && (
            <div className="space-y-5">
              {/* Pedidos section */}
              {detail.pedidos.length > 0 && (
                <div data-testid="pedidos-section">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-purple-700">
                    <ClipboardList className="w-4 h-4" /> Pedidos ({detail.pedidos.length})
                  </h3>
                  <div className="border rounded-lg">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-purple-50 text-purple-800">
                        <th className="px-3 py-2 text-left">N Pedido</th>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Solicitante</th>
                        <th className="px-3 py-2 text-center">Items</th>
                        <th className="px-3 py-2 text-center">Con OC</th>
                        <th className="px-3 py-2 text-left">OCs Vinculadas</th>
                      </tr></thead>
                      <tbody>
                        {detail.pedidos.map((p) => (
                          <tr key={p.numero} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono font-bold">{p.numero}</td>
                            <td className="px-3 py-2 text-gray-600">{fmtDate(p.fecha)}</td>
                            <td className="px-3 py-2">{p.solicitante || '-'}</td>
                            <td className="px-3 py-2 text-center">{p.total_items}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={p.items_con_oc === p.total_items && p.total_items > 0 ? 'text-green-600 font-semibold' : p.items_con_oc > 0 ? 'text-blue-600' : 'text-gray-400'}>
                                {p.items_con_oc}/{p.total_items}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {(p.ocs_vinculadas || []).map((oc) => (
                                  <span key={`oc-${oc}`} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-mono font-semibold border border-indigo-200">{oc}</span>
                                ))}
                                {(!p.ocs_vinculadas || p.ocs_vinculadas.length === 0) && <span className="text-gray-400">-</span>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* OC Cards */}
              {detail.ordenes_compra.length > 0 && (
                <div data-testid="ocs-section">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-indigo-700">
                    <FileText className="w-4 h-4" /> Ordenes de Compra ({detail.ordenes_compra.length})
                  </h3>
                  <div className="space-y-2">
                    {detail.ordenes_compra.map((oc) => (
                      <div key={oc.numero_oc} className="border rounded-lg p-3 hover:shadow-sm transition-shadow" data-testid={`oc-card-${oc.numero_oc}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-bold text-sm text-indigo-700">OC {oc.numero_oc}</span>
                            <OrigenBadge source={oc.source} />
                            <FlowBadge estado={oc.estado_flujo} />
                          </div>
                          <StepIndicator steps={[
                            { label: 'Emitida', done: true },
                            { label: 'Recibida', done: oc.recepciones?.length > 0 },
                            { label: 'Contabilizada', done: oc.contabilizaciones > 0 },
                          ]} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2 text-xs text-gray-600">
                          <div><span className="text-gray-400">Proveedor:</span> <strong>{oc.proveedor || '-'}</strong></div>
                          <div><span className="text-gray-400">Fecha:</span> {fmtDate(oc.fecha)}</div>
                          <div><span className="text-gray-400">Total:</span> <strong className="text-gray-800">{fmt(oc.total)}</strong></div>
                          <div><span className="text-gray-400">Cond. Pago:</span> {oc.condicion_pago || '-'}</div>
                          <div><span className="text-gray-400">Solicitante:</span> {oc.solicitante || '-'}</div>
                        </div>
                        {oc.recepciones?.length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 border-blue-300">
                            <p className="text-[10px] font-semibold text-blue-600 mb-1 flex items-center gap-1">
                              <Truck className="w-3 h-3" /> Recepciones ({oc.recepciones.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {oc.recepciones.map((r) => (
                                <span key={r.numero_recepcion} className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
                                  R-{r.numero_recepcion} | {fmtDate(r.fecha)} {r.guia_despacho ? `| GD ${r.guia_despacho}` : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {oc.facturas_alegra?.length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 border-green-300">
                            <p className="text-[10px] font-semibold text-green-600 mb-1 flex items-center gap-1">
                              <Receipt className="w-3 h-3" /> Facturas Alegra ({oc.facturas_alegra.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {oc.facturas_alegra.map((f) => (
                                <span key={f.numero} className="text-[10px] px-2 py-0.5 bg-green-50 text-green-700 rounded border border-green-200">
                                  #{f.numero} | {fmt(f.total)} | {fmtDate(f.fecha)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(!oc.recepciones || oc.recepciones.length === 0) && (
                          <p className="mt-1.5 text-[10px] text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Sin recepcion registrada
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.pedidos.length === 0 && detail.ordenes_compra.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">No se encontraron pedidos ni OCs para esta OT</p>
                </div>
              )}
            </div>
          )}

          {/* ═══ ITEMS TAB ═══ */}
          {detailTab === 'items' && (
            <div className="space-y-3" data-testid="items-tab">
              {itemsLoading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
              ) : itemsData ? (
                <>
                  {/* Items KPI bar */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="items-kpi">
                    <KpiCard title="Total Items" value={itemsData.resumen.total_items} icon={ClipboardList} color="bg-gray-100 text-gray-600" testId="kpi-items-total" />
                    <KpiCard title="Monto Total" value={fmt(itemsData.resumen.total_monto)} icon={DollarSign} color="bg-green-50 text-green-600" testId="kpi-items-monto" />
                    <KpiCard title="Materiales" value={itemsData.resumen.por_tipo?.material || 0} icon={Package} color="bg-indigo-50 text-indigo-600" testId="kpi-items-materiales" />
                    <KpiCard title="Servicios" value={itemsData.resumen.por_tipo?.servicio || 0} icon={Truck} color="bg-purple-50 text-purple-600" testId="kpi-items-servicios" />
                    <KpiCard title="Gastos" value={itemsData.resumen.por_tipo?.gasto || 0} icon={Receipt} color="bg-amber-50 text-amber-600" testId="kpi-items-gastos" />
                    <KpiCard title="Pend. Recepcion" value={itemsData.resumen.items_pendiente_recepcion} icon={AlertTriangle} color="bg-orange-50 text-orange-600" testId="kpi-items-pendientes" />
                    <KpiCard title="Alerta +30d" value={itemsData.resumen.items_con_alerta} icon={AlertTriangle} color="bg-red-50 text-red-600" testId="kpi-items-alerta" />
                  </div>

                  {/* Filters + View mode */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[180px] max-w-sm">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <Input placeholder="Buscar descripcion, codigo, proveedor, OC..." value={itemsSearch}
                        onChange={(e) => setItemsSearch(e.target.value)} className="pl-7 h-8 text-xs" data-testid="items-search" />
                    </div>
                    <select value={itemsEstadoFilter} onChange={(e) => setItemsEstadoFilter(e.target.value)}
                      className="h-8 px-2 rounded-md border border-input text-xs" data-testid="items-estado-filter">
                      <option value="">Todos los estados</option>
                      <option value="emitida">Emitida</option>
                      <option value="recibida">Recibida</option>
                      <option value="contabilizada">Contabilizada</option>
                    </select>
                    <select value={itemsTipoFilter} onChange={(e) => setItemsTipoFilter(e.target.value)}
                      className="h-8 px-2 rounded-md border border-input text-xs" data-testid="items-tipo-filter">
                      <option value="">Todos los tipos</option>
                      <option value="material">Material</option>
                      <option value="servicio">Servicio</option>
                      <option value="gasto">Gasto</option>
                    </select>
                    {/* View toggle */}
                    <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-md">
                      <button onClick={() => setItemsViewMode('tipo')} data-testid="view-tipo"
                        className={`px-2.5 py-1 rounded text-[11px] font-medium ${itemsViewMode === 'tipo' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500'}`}>
                        Por Tipo
                      </button>
                      <button onClick={() => setItemsViewMode('familia')} data-testid="view-familia"
                        className={`px-2.5 py-1 rounded text-[11px] font-medium ${itemsViewMode === 'familia' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500'}`}>
                        Por Familia
                      </button>
                      <button onClick={() => setItemsViewMode('tabla')} data-testid="view-tabla"
                        className={`px-2.5 py-1 rounded text-[11px] font-medium ${itemsViewMode === 'tabla' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500'}`}>
                        Tabla
                      </button>
                    </div>
                    <span className="text-xs text-gray-500">{sortedItems.length} items</span>
                  </div>

                  {/* ─── VIEW: Por Familia ─── */}
                  {itemsViewMode === 'familia' && (
                    <div className="space-y-3" data-testid="familia-view">
                      {(() => {
                        const byFamilia = {};
                        for (const it of sortedItems) {
                          const c = it.concepto || 'Sin clasificar';
                          if (!byFamilia[c]) byFamilia[c] = { items: [], monto: 0, tipo: it.tipo };
                          byFamilia[c].items.push(it);
                          byFamilia[c].monto += it.total_linea || 0;
                        }
                        const familias = Object.entries(byFamilia).sort((a, b) => b[1].monto - a[1].monto);
                        const tipoColors = { material: 'border-indigo-200 bg-indigo-50', servicio: 'border-purple-200 bg-purple-50', gasto: 'border-amber-200 bg-amber-50', sin_clasificar: 'border-gray-200 bg-gray-50' };
                        const tipoBadgeColors = { material: 'bg-indigo-100 text-indigo-700', servicio: 'bg-purple-100 text-purple-700', gasto: 'bg-amber-100 text-amber-700', sin_clasificar: 'bg-gray-100 text-gray-600' };
                        return familias.map(([nombre, data]) => (
                          <details key={nombre} className={`border rounded-lg overflow-hidden ${tipoColors[data.tipo] || tipoColors.sin_clasificar}`} data-testid={`familia-${nombre.slice(0,20)}`}>
                            <summary className="px-3 py-2.5 cursor-pointer hover:opacity-80 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${tipoBadgeColors[data.tipo] || tipoBadgeColors.sin_clasificar}`}>
                                  {data.tipo === 'material' ? 'MAT' : data.tipo === 'servicio' ? 'SRV' : data.tipo === 'gasto' ? 'GTO' : '?'}
                                </span>
                                <span className="text-sm font-semibold">{nombre}</span>
                                <span className="text-xs text-gray-500">({data.items.length} items)</span>
                              </div>
                              <span className="font-mono font-bold text-sm">{fmt(data.monto)}</span>
                            </summary>
                            <div className="bg-white border-t">
                              <table className="w-full text-xs">
                                <thead><tr className="bg-gray-100 text-gray-600">
                                  <th className="px-2 py-1.5 text-left">Descripcion</th>
                                  <th className="px-2 py-1.5 text-center w-12">Und</th>
                                  <th className="px-2 py-1.5 text-right w-14">Cant.</th>
                                  <th className="px-2 py-1.5 text-right w-20">V.Unit.</th>
                                  <th className="px-2 py-1.5 text-right w-24">Total</th>
                                  <th className="px-2 py-1.5 text-center w-14">OC</th>
                                  <th className="px-2 py-1.5 text-left w-32">Proveedor</th>
                                  <th className="px-2 py-1.5 text-center w-20">Estado</th>
                                </tr></thead>
                                <tbody>
                                  {data.items.map((it, j) => (
                                    <tr key={it.codigo || `det-${j}`} className="border-t hover:bg-gray-50">
                                      <td className="px-2 py-1.5 font-medium">{it.descripcion}</td>
                                      <td className="px-2 py-1.5 text-center">{it.unidad}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{it.cantidad}</td>
                                      <td className="px-2 py-1.5 text-right font-mono text-gray-600">{fmt(it.valor_unitario)}</td>
                                      <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmt(it.total_linea)}</td>
                                      <td className="px-2 py-1.5 text-center font-mono font-bold text-indigo-700"><OCNumber oc={it} size="sm" /></td>
                                      <td className="px-2 py-1.5 text-gray-600 truncate max-w-[150px]">{it.proveedor || '-'}</td>
                                      <td className="px-2 py-1.5 text-center"><FlowBadge estado={it.estado} /></td>
                                    </tr>
                                  ))}
                                  <tr className="bg-gray-50 font-semibold border-t">
                                    <td className="px-2 py-1.5" colSpan={4}>Subtotal {nombre}</td>
                                    <td className="px-2 py-1.5 text-right font-mono">{fmt(data.monto)}</td>
                                    <td colSpan={3}></td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </details>
                        ));
                      })()}
                    </div>
                  )}

                  {/* ─── VIEW: Por Tipo (Materiales / Servicios / Gastos) ─── */}
                  {itemsViewMode === 'tipo' && (
                    <div className="space-y-4" data-testid="tipo-view">
                      {[
                        { key: 'material', label: 'Materiales', color: 'bg-indigo-600', hdr: 'bg-indigo-800' },
                        { key: 'servicio', label: 'Servicios', color: 'bg-purple-600', hdr: 'bg-purple-800' },
                        { key: 'gasto', label: 'Gastos', color: 'bg-amber-600', hdr: 'bg-amber-800' },
                        { key: 'sin_clasificar', label: 'Sin Clasificar', color: 'bg-gray-500', hdr: 'bg-gray-700' },
                      ].map(({ key, label, hdr }) => {
                        const items = sortedItems.filter(i => i.tipo === key);
                        if (items.length === 0) return null;
                        const monto = items.reduce((s, i) => s + (i.total_linea || 0), 0);
                        return (
                          <div key={key} data-testid={`tipo-section-${key}`}>
                            <div className={`${hdr} text-white px-3 py-2 rounded-t-lg flex items-center justify-between`}>
                              <span className="font-semibold text-sm">{label} ({items.length})</span>
                              <span className="font-mono font-bold">{fmt(monto)}</span>
                            </div>
                            <div className="border border-t-0 rounded-b-lg overflow-x-auto">
                              <ItemsTable items={items} sortBy={itemsSortBy} sortDir={itemsSortDir} onSort={handleItemsSort} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ─── VIEW: Tabla plana ─── */}
                  {itemsViewMode === 'tabla' && (
                    <div className="border rounded-lg overflow-x-auto" data-testid="items-table">
                      <ItemsTable items={sortedItems} sortBy={itemsSortBy} sortDir={itemsSortDir} onSort={handleItemsSort} showTipo />
                    </div>
                  )}
                  {sortedItems.length === 0 && (
                    <p className="text-center text-sm text-gray-500 py-8">No se encontraron items con los filtros aplicados</p>
                  )}
                </>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-lg">
                  <ListFilter className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">Error cargando items</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
